/**
 * Practitioner Client View Screen — Screen 2 of the Practitioner Portal.
 *
 * Shows a client's analysis history, session prep notes, pattern trends,
 * and (Phase 2) Sendbird-backed chat thread.
 *
 * Architecture:
 * - GlassBackground as root (same aurora gradient as dashboard)
 * - ClientViewHeader sticky at top (back + client name + consent pill)
 * - ScrollView with all content sections below
 * - Consent-gated sections respect client's sharing preferences
 *
 * Data: usePractitionerClientView() — mock data until DB schema migration runs.
 * Analytics events per SPEC.md §5.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '@/lib/analytics/events';
import { GlassBackground } from '../../../components/glass/GlassBackground';
import { useTabScreenPadding } from '../../../components/layout/TabScreenWrapper';
import { usePractitionerClientView } from '../../../hooks/usePractitionerClientView';
import {
  ClientViewHeader,
  ClientStatsStrip,
  SessionPrepCard,
  PatternTrendsCard,
  AnalysisHistoryCard,
  CommentThread,
} from '../../../components/screens/practitioner-client-view';

export default function PractitionerClientViewScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { t } = useTranslation('practitioner');
  const bottomPadding = useTabScreenPadding();

  const {
    clientName,
    clientInitials: _clientInitials,
    consentLevel,
    memberSince,
    analysisCount,
    tierName,
    sessionPrepNotes,
    patternTrends,
    analysisHistory,
    chatMessages,
    consent,
  } = usePractitionerClientView(id);

  // Track screen view on mount
  useEffect(() => {
    trackEvent('Screen_View_PractitionerClientView', { client_id: id ?? '' });
  }, [id]);

  const handleBack = useCallback(() => {
    trackEvent('Button_Click_PractitionerBackToList', {});
    router.back();
  }, [router]);

  const handleAnalysisPress = useCallback((_analysisId: string) => {
    // TODO: Navigate to individual analysis detail screen once route is built.
  }, []);

  const handleChatSend = useCallback((_text: string) => {
    // TODO: Wire to Sendbird SDK in Phase 2.
  }, []);

  const contentContainerStyle = useMemo(
    () => [styles.scrollContent, { paddingBottom: bottomPadding }],
    [bottomPadding]
  );

  // Build history items with bound press handler
  const historyItems = useMemo(
    () =>
      analysisHistory.map((item) => ({
        ...item,
        onPress: handleAnalysisPress,
      })),
    [analysisHistory, handleAnalysisPress]
  );

  return (
    <GlassBackground>
      <Stack.Screen options={{ headerShown: false }} />

      <View style={styles.root}>
        {/* Sticky header */}
        <ClientViewHeader
          clientName={clientName}
          consentLevel={consentLevel}
          onBack={handleBack}
          t={t}
        />

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={contentContainerStyle}
          showsVerticalScrollIndicator={false}
        >
          {/* Client stats strip */}
          <ClientStatsStrip
            memberSince={memberSince}
            analysisCount={analysisCount}
            tier={tierName}
            t={t}
          />

          {/* AI session prep notes — consent-gated */}
          {consent.canViewHistory && (
            <SessionPrepCard notes={sessionPrepNotes} t={t} />
          )}

          {/* Pattern trends — consent-gated */}
          {consent.canViewHistory && (
            <PatternTrendsCard trends={patternTrends} t={t} />
          )}

          {/* Analysis history — always shown, consent affects body */}
          <AnalysisHistoryCard
            items={historyItems}
            totalCount={analysisCount}
            canViewHistory={consent.canViewHistory}
            canViewOriginalMessage={consent.canViewOriginalMessage}
            clientName={clientName}
            t={t}
          />

          {/* Comment thread — hidden when canChat = false */}
          <CommentThread
            clientName={clientName}
            messages={chatMessages}
            canChat={consent.canChat}
            onSend={handleChatSend}
            t={t}
          />
        </ScrollView>
      </View>
    </GlassBackground>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  } as ViewStyle,
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 8,
    gap: 16,
  },
});
